import CashPage from "./pages/forecast/CashPage";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";

import { ThemeProvider } from "./lib/theme.jsx";
import { RoleProvider, useRole } from "./lib/role.jsx";
import { GateProvider, useGate } from "./lib/gate.jsx";
import { CopilotProvider, useCopilot } from "./lib/copilot.jsx";
import { DiscoveryProvider } from "./lib/discovery.jsx";
import AppShell from "./components/AppShell";
import CopilotPanel from "./components/CopilotPanel";
import ErrorBoundary from "./components/ErrorBoundary";

import GatePage from "./pages/GatePage";
import CommandCenter from "./pages/CommandCenter";
import BranchPerformance from "./pages/BranchPerformance";
import BranchDetail from "./pages/BranchDetail";
import InventoryIntelligence from "./pages/InventoryIntelligence";
import ProcurementSupply from "./pages/ProcurementSupply";
import Omnichannel from "./pages/Omnichannel";
import Financial from "./pages/Financial";
import Merchandising from "./pages/Merchandising";
import Forecast from "./pages/Forecast";
import ForecastOverview from "./pages/forecast/Overview";
import ModelPage from "./pages/forecast/ModelPage";
import SourceView from "./pages/forecast/SourceView";
import Anomalies from "./pages/Anomalies";
import Operations from "./pages/Operations";
import DigitalTwin from "./pages/DigitalTwin";
import Workbench from "./pages/Workbench";
import KpiFramework from "./pages/KpiFramework";
import ReportsHub from "./pages/ReportsHub";
import MorningBrief from "./pages/MorningBrief";
import AccessRestricted from "./pages/AccessRestricted";
import LiveDataPipeline from "./pages/admin/LiveDataPipeline";
import SemanticExplorer from "./pages/admin/SemanticExplorer";
import DataManagement from "./pages/admin/DataManagement";
import EnginesAdmin from "./pages/admin/EnginesAdmin";
import CostTransparency from "./pages/admin/CostTransparency";
import ApiKeysAdmin from "./pages/admin/ApiKeysAdmin";
import ModuleVisibility from "./pages/admin/ModuleVisibility";
import ClassificationRules from "./pages/admin/ClassificationRules";
import ModulePaused from "./pages/ModulePaused";
import KnowledgeCenter from "./pages/KnowledgeCenter";
import { ModuleVisibilityProvider } from "./lib/modules.jsx";

// Route guard — checks the user's role against the visibility matrix.
// /branches/:id is folded into /branches for guard purposes.
function Guard({ path, children }) {
  const { canAccess, isHidden } = useRole();
  const location = useLocation();
  const effective = path || location.pathname;
  if (isHidden(effective)) return <ModulePaused />;
  if (!canAccess(effective)) return <AccessRestricted />;
  return children;
}

function Shell() {
  const { open, openCopilot, closeCopilot } = useCopilot();
  return (
    <BrowserRouter>
      <AppShell onOpenCopilot={openCopilot}>
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Guard path="/"><CommandCenter /></Guard>} />
            <Route path="/morning-brief" element={<Guard path="/morning-brief"><MorningBrief /></Guard>} />
            <Route path="/branches" element={<Guard path="/branches"><BranchPerformance /></Guard>} />
            <Route path="/branches/:id" element={<Guard path="/branches"><BranchDetail /></Guard>} />
            <Route path="/inventory" element={<Guard path="/inventory"><InventoryIntelligence /></Guard>} />
            <Route path="/procurement" element={<Guard path="/procurement"><ProcurementSupply /></Guard>} />
            <Route path="/omnichannel" element={<Guard path="/omnichannel"><Omnichannel /></Guard>} />
            <Route path="/financial" element={<Guard path="/financial"><Financial /></Guard>} />
            <Route path="/reports" element={<Guard path="/reports"><ReportsHub /></Guard>} />
            <Route path="/merchandising" element={<Guard path="/merchandising"><Merchandising /></Guard>} />
            <Route path="/forecast" element={<Guard path="/forecast"><Forecast /></Guard>} />
            <Route path="/forecast/overview" element={<Guard path="/forecast"><ForecastOverview /></Guard>} />
            <Route path="/forecast/stockout" element={<Guard path="/forecast"><ModelPage key="stockout" model="stockout" /></Guard>} />
            <Route path="/forecast/reorder" element={<Guard path="/forecast"><ModelPage key="reorder" model="reorder" /></Guard>} />
            <Route path="/forecast/aging" element={<Guard path="/forecast"><ModelPage key="aging" model="aging" /></Guard>} />
            <Route path="/forecast/weather" element={<Guard path="/forecast"><ModelPage key="weather" model="weather" /></Guard>} />
            <Route path="/forecast/cash" element={<Guard path="/forecast"><CashPage /></Guard>} />
            <Route path="/forecast/source" element={<Guard path="/forecast"><SourceView /></Guard>} />
            <Route path="/anomalies" element={<Guard path="/anomalies"><Anomalies /></Guard>} />
            <Route path="/operations" element={<Guard path="/operations"><Operations /></Guard>} />
            <Route path="/twin" element={<Guard path="/twin"><DigitalTwin /></Guard>} />
            <Route path="/workbench" element={<Guard path="/workbench"><Workbench /></Guard>} />
            <Route path="/kpi" element={<Guard path="/kpi"><KpiFramework /></Guard>} />
            <Route path="/admin/pipeline" element={<Guard path="/admin/pipeline"><LiveDataPipeline /></Guard>} />
            <Route path="/admin/explorer" element={<Guard path="/admin/explorer"><SemanticExplorer /></Guard>} />
            <Route path="/admin/data-management" element={<Guard path="/admin/data-management"><DataManagement /></Guard>} />
            <Route path="/admin/engines" element={<Guard path="/admin/engines"><EnginesAdmin /></Guard>} />
            <Route path="/admin/classification" element={<Guard path="/admin/classification"><ClassificationRules /></Guard>} />
            <Route path="/admin/cost-transparency" element={<Guard path="/admin/cost-transparency"><CostTransparency /></Guard>} />
            <Route path="/admin/api-keys" element={<Guard path="/admin/api-keys"><ApiKeysAdmin /></Guard>} />
            <Route path="/admin/modules" element={<Guard path="/admin/modules"><ModuleVisibility /></Guard>} />
            {/* Knowledge Center: every signed-in role, never in the sidebar, not affected by module visibility */}
            <Route path="/learn/*" element={<KnowledgeCenter />} />
            <Route path="*" element={<AccessRestricted />} />
          </Routes>
        </ErrorBoundary>
      </AppShell>
      <CopilotPanel open={open} onClose={closeCopilot} />
    </BrowserRouter>
  );
}

function ProtectedApp() {
  const { authed } = useGate();
  if (!authed) return <GatePage />;
  return (
    <ModuleVisibilityProvider>
      <RoleProvider>
        <CopilotProvider>
          <DiscoveryProvider>
            <Shell />
          </DiscoveryProvider>
        </CopilotProvider>
      </RoleProvider>
    </ModuleVisibilityProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <GateProvider>
        <ProtectedApp />
      </GateProvider>
    </ThemeProvider>
  );
}
